import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

interface Category {
  id: string;
  name: string;
}

interface Metric {
  id: string;
  categoryId: string;
  name: string;
  slug: string;
  sourceType: string;
  isActive: boolean;
  dataProvider: string | null;
}

export function MetricList() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch("/api/admin/categories");
      const data = await res.json();
      setCategories(data.data ?? []);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const url =
        selectedCategory !== "all"
          ? `/api/admin/metrics?categoryId=${selectedCategory}`
          : "/api/admin/metrics";
      const res = await fetch(url);
      const data = await res.json();
      setMetrics(data.data ?? []);
    } catch {
      toast.error("Failed to load metrics");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  async function toggleActive(metric: Metric) {
    try {
      const res = await fetch(`/api/admin/metrics/${metric.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !metric.isActive }),
      });
      if (!res.ok) throw new Error();
      setMetrics((prev) =>
        prev.map((m) => (m.id === metric.id ? { ...m, isActive: !m.isActive } : m)),
      );
    } catch {
      toast.error("Update failed");
    }
  }

  const sourceColor = (type: string) => {
    if (type === "automated") return "default" as const;
    if (type === "manual") return "secondary" as const;
    return "outline" as const;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-full sm:w-[250px]">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                {cat.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground self-center">{metrics.length} metrics</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b text-left text-sm text-muted-foreground">
                <th className="pb-3 font-medium">Metric</th>
                <th className="pb-3 font-medium">Source</th>
                <th className="pb-3 font-medium">Provider</th>
                <th className="pb-3 font-medium">Active</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <tr key={m.id} className="border-b">
                  <td className="py-3 font-medium">{m.name}</td>
                  <td className="py-3">
                    <Badge variant={sourceColor(m.sourceType)}>{m.sourceType}</Badge>
                  </td>
                  <td className="py-3 text-sm text-muted-foreground">
                    {m.dataProvider ?? "\u2014"}
                  </td>
                  <td className="py-3">
                    <Switch checked={m.isActive} onCheckedChange={() => toggleActive(m)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
